import { Redirect, type UnknownOutputParams } from 'expo-router'
import { useState, type ReactNode } from 'react'
import { View, Platform, StyleSheet } from 'react-native'
import Animated from 'react-native-reanimated'

import type { TAnimatedParallaxListProps } from '@/src/elements/animated-parallax-list/AnimatedParallaxList'
import type { TGetPublicationsData } from '@/src/features/publication/api/types/getPublications.types'
import type { TEventOriginParam } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'
import { PUBLICATION_TRACKING_SOURCE, TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import { COMMUNITY_LIFETIME } from '../../../models/community.constants'
import { useCommunityQuery } from '../../../services/useCommunityQuery'
import { useCommunitySubscriptionsQuery } from '../../../services/useCommunitySubscriptionsQuery'
import { usePublicationsOfCommunityQuery } from '../../../services/usePublicationsOfCommunityQuery'
import { CommunityEmptyMessage } from '../../components/CommunityEmptyMessage/CommunityEmptyMessage'
import { CommunityPublicationItem } from '../../components/CommunityPublicationItem'
import { useAutoScroll } from '../../hooks/useAutoScroll'
import { useListAnimatedScroll } from '../../hooks/useListAnimatedScroll'
import { CommunityActionButton } from './CommunityActionButton/CommunityActionButton'
import { CommunityCompactHeader } from './CommunityCompactHeader'
import { CommunityLeave } from './CommunityLeave'
import { CommunitySearchParamsShare } from './CommunitySearchParamsShare'
import { CommunityHeader } from './ListHeader/CommunityHeader'
import { ListHeader } from './ListHeader/ListHeader'
import { PublicationPlaceholders } from './PublicationsPlaceholders'
import { useCommunityRedirections } from './useCommunityRedirections'
import { useIncrementCommunityViews } from './useIncrementCommunityViews'

type TPublicationListItem = TGetPublicationsData<'sharesCount'>[number]

type TCommunityScreenProps = TEventOriginParam & {
  id: number
  action?: string
  slug?: string
  searchParams: UnknownOutputParams
}

export const CommunityScreen = ({ id, action, origin, slug, searchParams }: TCommunityScreenProps): ReactNode => {
  const [showCommunityLeaveModal, setShowCommunityLeaveModal] = useState(false)
  const { routes } = useRoutes()
  useIncrementCommunityViews(id)
  const { xs, sm } = useDimensions()
  const { redirect } = filterSearchParams(searchParams)

  const communityQuery = useCommunityQuery(id)
  const community = communityQuery.data
  const { activeSubscription: subscription, isLoading: isSubscriptionLoading } = useCommunitySubscriptionsQuery(
    id,
    community?.lifetime,
  )
  const publications = usePublicationsOfCommunityQuery({
    id,
    type: community?.lifetime,
    enabled: !!communityQuery.isSuccess,
  })

  const nextPublicationIndex =
    subscription?.nextPublication && publications.data
      ? publications.data.findIndex((p) => p.id === subscription.nextPublication!.id)
      : -1

  const {
    headerAnimatedStyle,
    scrollHandler,
    offsetY,
    listRef,
    handleScrollBegin,
    handleScrollEnd,
    handleScrollEndDrag,
    fixedOffsetY,
  } = useListAnimatedScroll()

  useAutoScroll(listRef, nextPublicationIndex, publications.isLoading || isSubscriptionLoading)

  const { redirectPath } = useCommunityRedirections({
    community: communityQuery.data,
    forbidden: communityQuery.error?.name === 'Forbidden',
    id,
    origin,
    slug,
  })

  if (redirectPath) return <Redirect href={redirectPath} />

  if (communityQuery.isError)
    return (
      <ErrorScreen
        error={communityQuery.error}
        defaultErrorKey="community.load.error"
        fallbackRoute={routes.communities()}
      />
    )

  const isEverlasting = community?.lifetime === COMMUNITY_LIFETIME.EVERLASTING

  const ListFooter = () =>
    publications.isFetchingNextPage || publications.isPending ? (
      <View className="mt-4">
        <PublicationPlaceholders />
      </View>
    ) : (
      <View className="h-24" />
    )

  const ListEmptyComponent: TAnimatedParallaxListProps<TPublicationListItem>['ListEmptyComponent'] = () =>
    community &&
    publications.data &&
    publications.data.length === 0 && (
      <CommunityEmptyMessage isEverlasting={isEverlasting} lifetime={community.lifetime} />
    )

  const renderItem: TAnimatedParallaxListProps<TPublicationListItem>['renderItem'] = ({ item: publicationItem }) =>
    !community ? null : (
      <CommunityPublicationItem community={community} publication={publicationItem} subscription={subscription} />
    )

  const communityHeader = (
    <CommunityHeader
      community={community}
      onClickLeaveCommunity={() => setShowCommunityLeaveModal(true)}
      redirect={redirect}
      sharing={{
        picture: community?.picture,
        name: community?.name,
        trackingSource: TRACKING_SOURCE.COMMUNITY_PAGE,
      }}
    />
  )

  const mobileViewPortStyle = [
    { zIndex: 20 },
    communityStyle.mobileHeader,
    communityStyle.bottomRadius,
    Platform.OS !== 'web' ? headerAnimatedStyle : {},
  ]

  const isMobileViewPort = xs || sm

  return (
    <>
      <View testID={TEST_ID.COMMUNITY.SCREEN} className="min-w-screen h-full flex-1">
        {community && <CommunitySearchParamsShare {...community} searchParams={searchParams} />}

        <CommunityCompactHeader
          community={community}
          header={communityHeader}
          offsetY={offsetY}
          searchParams={searchParams}
          subscription={subscription}
        />

        <Animated.FlatList
          ref={listRef}
          data={publications.data || []}
          keyExtractor={(item) => item.id.toString()}
          indicatorStyle="white"
          onScroll={isMobileViewPort ? scrollHandler : undefined}
          onEndReachedThreshold={0.5}
          stickyHeaderIndices={Platform.OS === 'web' ? [] : [0]}
          ListHeaderComponent={
            <>
              <Animated.View
                className={!isMobileViewPort ? 'h-[420px] web:max-w-3xl web:w-3/4 web:w-full web:self-center z-30' : ''}
                style={isMobileViewPort ? mobileViewPortStyle : []}
              >
                <ListHeader
                  community={community}
                  offsetY={isMobileViewPort ? offsetY : fixedOffsetY}
                  searchParams={searchParams}
                  subscription={subscription}
                  animated={isMobileViewPort && Platform.OS !== 'web'}
                />
              </Animated.View>
              <View className="absolute z-40 w-full">{communityHeader}</View>
            </>
          }
          ItemSeparatorComponent={() => <></>}
          ListFooterComponent={ListFooter}
          onMomentumScrollEnd={handleScrollEnd}
          onMomentumScrollBegin={handleScrollBegin}
          onScrollBeginDrag={handleScrollBegin}
          onScrollEndDrag={handleScrollEndDrag}
          scrollEventThrottle={16}
          onEndReached={() =>
            !publications.isFetchingNextPage && publications.hasNextPage && publications.fetchNextPage()
          }
          onScrollToIndexFailed={({ index, averageItemLength }) => {
            listRef.current?.scrollToOffset({ offset: averageItemLength * index, animated: true })
          }}
          renderItem={renderItem}
          onTouchStart={Platform.OS === 'web' ? handleScrollBegin : undefined}
          onTouchEnd={Platform.OS === 'web' ? handleScrollEnd : undefined}
          ListEmptyComponent={ListEmptyComponent}
        />
        <CommunityActionButton
          communityId={id}
          community={community}
          course="page"
          origin={origin}
          source={TRACKING_SOURCE.COMMUNITY_PAGE}
          publicationTrackingSource={PUBLICATION_TRACKING_SOURCE.COMMUNITY_READ_MORE}
        />
      </View>
      <CommunityLeave
        id={id}
        action={action}
        showCommunityLeaveModal={showCommunityLeaveModal}
        setShowCommunityLeaveModal={setShowCommunityLeaveModal}
        community={community}
      />
    </>
  )
}

const communityStyle = StyleSheet.create({
  mobileHeader: {
    zIndex: 20,
    right: 0,
    left: 0,
    backgroundColor: 'white',
    boxShadow: '4px 0px 8px 0px rgba(0, 0, 0, 0.65)',
    height: Platform.OS === 'web' ? 420 : 400,
  },
  bottomRadius: {
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
})
